/**
 * Printed Deliverables sheet — one page per day: every deliverable MERGED
 * across all schools with summed quantities (the Deliverables tab, printed),
 * for the packing team prepping batches in advance (Yanping Ren / Masha).
 *
 * Same table anatomy as the per-school campus sheet (Qty | Item with
 * packaging → portion sublabel | Temp) minus the transport temp-record
 * boxes — this sheet lives at the packing bench, not in the truck.
 *
 * Milk left the mealtimes for each campus's milk card (PROD-1502), so the
 * page carries it as its own block on top, summed across schools per milk
 * item; without it the moved milk would not print here at all.
 */

import { MealtimeHeader } from '@/components/chefs/mealtime-header'
import { PrintHeader } from '@/components/print/print-header'
import { temperatureLabel } from '@/lib/chefs/format'
import { formatDeliverableQuantity } from '@/lib/chefs/format'
import { mergeCampusesMilk } from '@/lib/chefs/milk'
import { deliverableTypeGroup, groupByTypeAndTemp, PACK_TYPE_LABELS } from '@/lib/chefs/pack-groups'
import { deliverablesBlockId, formatLongDate } from '@/lib/chefs/print-plan'
import { computeTotalMeals, mergeCampusesMealtimes } from '@/lib/chefs/totals'
import type { Campus } from '@/lib/chefs/types'
import { MEALTIME_ORDER } from '@/lib/chefs/types'

interface PrintDeliverablesPageProps {
  iso: string
  date: Date
  campuses: Campus[]
  kitchenName: string
  dateLabel: string
}

export function PrintDeliverablesPage({ iso, date, campuses, kitchenName, dateLabel }: PrintDeliverablesPageProps) {
  const blocks = mergeCampusesMealtimes(campuses).sort(
    (a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime),
  )
  const milk = mergeCampusesMilk(campuses)
  if (blocks.length === 0 && milk.length === 0) {
    return null
  }
  const meals = computeTotalMeals(campuses)

  return (
    <article data-print-block={deliverablesBlockId(iso)} className="print-page-break font-sans text-black">
      <PrintHeader kitchenName={kitchenName} dateLabel={dateLabel} />

      <header className="mb-4 flex items-baseline justify-between gap-3 border-b border-black pb-2">
        <h1 className="text-2xl font-semibold">Deliverables for {formatLongDate(date)}</h1>
        <p className="text-sm">
          <span className="font-semibold">{meals.toLocaleString()}</span> meals ·{' '}
          <span className="font-semibold">{campuses.length}</span> school{campuses.length === 1 ? '' : 's'}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {milk.length > 0 ? (
          <section
            data-testid="print-deliverables-milk"
            className="overflow-hidden rounded-lg border border-border break-inside-avoid">
            <div className="flex items-baseline gap-2 border-b border-border px-2 py-1.5">
              <span className="text-sm font-semibold">Milk</span>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {milk.map((row) => (
                  <tr key={row.key}>
                    <td className="w-16 whitespace-nowrap px-2 py-1 align-middle font-medium">
                      {row.qty.toLocaleString()}x
                    </td>
                    <td className="px-2 py-1 align-middle">
                      {row.name}
                      {row.packaging || row.servingSize ? (
                        <span className="block text-[10px] leading-snug text-muted-foreground">
                          {[row.packaging, row.servingSize ? `${row.servingSize} per serving` : null]
                            .filter(Boolean)
                            .join(' → ')}
                        </span>
                      ) : null}
                    </td>
                    <td className="w-14 px-2 py-1 align-middle text-xs text-muted-foreground">
                      {temperatureLabel(row.temperature)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
        {blocks.map((block) => (
          <section key={block.mealtime} className="overflow-hidden rounded-lg border border-border break-inside-avoid">
            <MealtimeHeader mealtime={block.mealtime} count={block.meals} />
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="w-16 px-2 py-1 text-left font-medium">Qty</th>
                  <th className="px-2 py-1 text-left font-medium">Item</th>
                  <th className="w-14 px-2 py-1 text-left font-medium">Temp</th>
                </tr>
              </thead>
              {/* Same default grouping as the on-screen Deliverables page:
                  TYPE sections (Mains include any package with a main in it),
                  rows ORDERED hot → chilled → ambient inside each. */}
              {groupByTypeAndTemp(
                [...block.deliverables].sort((a, z) => a.name.localeCompare(z.name)),
                deliverableTypeGroup,
                (d) => d.temperature,
              ).map((typeGroup) => (
                <tbody key={typeGroup.group} className="divide-y divide-border">
                  <tr className="break-inside-avoid bg-muted/40">
                    <td
                      colSpan={3}
                      className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {PACK_TYPE_LABELS[typeGroup.group]} · {typeGroup.items.length} SKU
                      {typeGroup.items.length === 1 ? '' : 's'}
                    </td>
                  </tr>
                  {typeGroup.items.map((d) => (
                    <tr key={d.id}>
                      <td className="whitespace-nowrap px-2 py-1 align-middle font-medium">
                        {formatDeliverableQuantity(d)}
                      </td>
                      <td className="px-2 py-1 align-middle">
                        {d.name}
                        {d.packaging || d.servingSize ? (
                          <span className="block text-[10px] leading-snug text-muted-foreground">
                            {[d.packaging, d.servingSize ? `${d.servingSize} per serving` : null]
                              .filter(Boolean)
                              .join(' → ')}
                          </span>
                        ) : null}
                        {d.condiments ? (
                          <span className="block text-[10px] leading-snug text-muted-foreground">{d.condiments}</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-1 align-middle text-xs text-muted-foreground">
                        {temperatureLabel(d.temperature)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </section>
        ))}
      </div>
    </article>
  )
}
